import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CartSummary as CartSummaryType, CartItem } from '../hooks/useCart';
import { MINIMUM_ORDER_VALUE, QUANTITY_DISCOUNT_THRESHOLD, QUANTITY_DISCOUNT_AMOUNT } from '../hooks/useCart';
import {
  ShoppingCart,
  ChevronUp,
  ChevronDown,
  Trash2,
  Check,
  Loader2,
  Send,
  Tag,
} from 'lucide-react';

interface CartSummaryProps {
  summary: CartSummaryType;
  items: CartItem[];
  isSubmitting: boolean;
  onSubmit: () => void;
  onClear: () => void;
}

export default function CartSummary({
  summary,
  items,
  isSubmitting,
  onSubmit,
  onClear,
}: CartSummaryProps) {
  const isEmpty = items.length === 0;
  const [expanded, setExpanded] = useState(true);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky bottom-0 mt-6 z-20"
    >
      {/* Expandable Cart Details */}
      <AnimatePresence>
        {!isEmpty && expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="bg-workshop-surface rounded-t-sm shadow-lift border border-b-0 border-white/[0.08]">
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-display text-cream uppercase tracking-wide text-sm flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-accent" />
                    Cart ({summary.itemCount} items)
                  </h3>
                  <button
                    onClick={onClear}
                    className="text-xs text-text-muted hover:text-red-400 uppercase tracking-wide transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3 h-3" />
                    Clear
                  </button>
                </div>

                {/* Cart Items Preview */}
                <div className="max-h-36 overflow-y-auto mb-3 space-y-1 scrollbar-thin">
                  {items.map((item, i) => (
                    <motion.div
                      key={item.variantId}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="flex justify-between items-center text-sm py-1.5 px-2 rounded-sm hover:bg-white/[0.03] border-b border-white/[0.03] last:border-0"
                    >
                      <div className="flex-1 truncate mr-4">
                        <span className="text-cream">{item.productTitle}</span>
                        {item.variantTitle !== 'Default Title' && (
                          <span className="text-text-muted ml-1 text-xs">({item.variantTitle})</span>
                        )}
                        <span className="text-text-muted ml-2 text-xs bg-white/[0.04] px-1.5 py-0.5 rounded-sm">
                          &times;{item.quantity}
                        </span>
                      </div>
                      <span className="font-medium text-accent text-sm tabular-nums">
                        ${(item.wholesalePrice * item.quantity).toFixed(2)}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t border-white/[0.08] pt-3 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-text-muted">Retail Total:</span>
                    <span className="line-through text-text-muted tabular-nums">
                      ${summary.retailTotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-muted">Wholesale:</span>
                    <span className="text-cream tabular-nums">
                      ${summary.wholesaleTotal.toFixed(2)}
                    </span>
                  </div>
                  {summary.quantityDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-text-muted">
                        Qty Discount ({summary.itemsWithQuantityDiscount} item{summary.itemsWithQuantityDiscount > 1 ? 's' : ''}):
                      </span>
                      <span className="text-green-400 font-medium tabular-nums">
                        -${summary.quantityDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-1.5 border-t border-white/[0.04]">
                    <span className="text-text-muted">Total Savings:</span>
                    <span className="text-green-400 font-medium tabular-nums">
                      -${summary.savings.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Quantity Discount Info */}
                <div className="mt-3 pt-3 border-t border-white/[0.06]">
                  <div className="bg-accent/[0.08] rounded-sm p-2.5 flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                    <p className="text-xs text-accent font-medium">
                      Order {QUANTITY_DISCOUNT_THRESHOLD}+ of any item and save ${QUANTITY_DISCOUNT_AMOUNT.toFixed(2)} per unit!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Summary Bar */}
      <div
        className={`
          bg-workshop-surface text-cream p-4 shadow-lg border border-white/[0.08]
          ${isEmpty ? 'rounded-sm' : expanded ? 'rounded-b-sm border-t-0' : 'rounded-sm'}
        `}
      >
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Left: Toggle + Minimum Order Info */}
          <div className="flex items-center gap-4 text-center md:text-left w-full md:w-auto">
            {/* Toggle button */}
            {!isEmpty && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-2 text-text-muted hover:text-accent rounded-sm hover:bg-white/[0.04] transition-colors"
                title={expanded ? 'Collapse cart' : 'Expand cart'}
              >
                {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            )}

            <div>
              {isEmpty ? (
                <p className="text-text-muted text-sm flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Add items to your cart to place an order
                </p>
              ) : !summary.meetsMinimum ? (
                <div>
                  <p className="text-text-muted text-sm uppercase tracking-wide">
                    Minimum Order: ${MINIMUM_ORDER_VALUE.toFixed(2)}
                  </p>
                  <p className="text-accent text-sm mt-0.5 font-medium">
                    {(summary.finalTotal / MINIMUM_ORDER_VALUE) >= 0.5
                      ? `Almost there! Just $${summary.amountToMinimum.toFixed(2)} more`
                      : `Add $${summary.amountToMinimum.toFixed(2)} more to checkout`
                    }
                  </p>
                </div>
              ) : (
                <p className="text-text-muted text-sm uppercase tracking-wide flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-green-900/40 flex items-center justify-center">
                    <Check className="w-3 h-3 text-green-400" />
                  </span>
                  Minimum Order Met
                </p>
              )}
              <p className="text-xl font-display mt-1">
                {summary.quantityDiscount > 0 ? (
                  <>
                    Order Total:{' '}
                    <span className="line-through text-text-muted text-base mr-2">
                      ${summary.wholesaleTotal.toFixed(2)}
                    </span>
                    <span className="text-accent">${summary.finalTotal.toFixed(2)}</span>
                  </>
                ) : (
                  <>
                    Wholesale Total:{' '}
                    <span className="text-accent">${summary.finalTotal.toFixed(2)}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Right: Progress + Submit Button */}
          <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
            {/* Progress to MOQ */}
            {!isEmpty && !summary.meetsMinimum && (
              <div className="w-full md:w-48">
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-accent to-accent-light rounded-full"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.min(
                        (summary.finalTotal / MINIMUM_ORDER_VALUE) * 100,
                        100
                      )}%`,
                    }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
                <p className="text-[11px] text-text-muted mt-1 text-center tabular-nums">
                  ${summary.finalTotal.toFixed(2)} / ${MINIMUM_ORDER_VALUE.toFixed(2)}
                </p>
              </div>
            )}

            <motion.button
              whileHover={!isEmpty && summary.meetsMinimum && !isSubmitting ? { scale: 1.02 } : {}}
              whileTap={!isEmpty && summary.meetsMinimum && !isSubmitting ? { scale: 0.98 } : {}}
              onClick={onSubmit}
              disabled={isEmpty || !summary.meetsMinimum || isSubmitting}
              className={`
                px-8 py-3 uppercase font-semibold tracking-wide rounded-sm
                transition-all duration-200 flex items-center gap-2 text-sm
                ${
                  isEmpty || !summary.meetsMinimum
                    ? 'bg-white/10 text-text-muted cursor-not-allowed'
                    : 'bg-accent text-workshop hover:bg-accent-light active:bg-accent-dark shadow-md hover:shadow-lg'
                }
                ${isSubmitting ? 'cursor-wait' : ''}
              `}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Order
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
